/* eslint-disable react/no-array-index-key */
import { addOneReportObject } from '@brands/services/oneReport/addOneReportObject';
import { deleteOneReportObject } from '@brands/services/oneReport/deleteOneReportObject';
import { IEducation } from '@brands/services/oneReport/types/IEducation';
import { ObjectType, updateOneReportObject } from '@brands/services/oneReport/updateOneReportObject';
import { displayErrorDetails } from '@brands/Utils/displayError';
import AddIcon from '@mui/icons-material/Add';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Button, Divider, Menu, MenuItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';

import { ICase } from '../../../../services/cases/types/ICase';
import { UserProfile } from '../../../../services/identity/types/UserProfile';
import { checkCollapsibleHeaderColor } from '../../../../Utils/getCollapsibleHeader';
import { Education, OralHealthReportSubmit } from '../../assets/types';
import styles from '../../styles.module.scss';
import Book from '../PossibleTreatmentNeedsSection/icons/book.svg';
import EducationModal, { EducationForm } from './modal/EducationModal';
import ConfirmActionModal from '../../Modals/ConfirmActionModal/ConfirmActionModal';

type EducationSectionProps = {
  consultationForm: OralHealthReportSubmit;
  setConsultationForm: React.Dispatch<React.SetStateAction<OralHealthReportSubmit>>;
  thisCase: ICase;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  userInfo: UserProfile;
  register: UseFormRegister<OralHealthReportSubmit>;
  setValue: UseFormSetValue<OralHealthReportSubmit>;
  trigger: UseFormTrigger<OralHealthReportSubmit>;
  errors: FieldErrors<OralHealthReportSubmit>;
};

export const normalizeUrl = (url?: string): string => {
  if (!url?.trim()) return '';

  const trimmedUrl = url.trim();

  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
};

const EducationSection = ({
  consultationForm,
  setConsultationForm,
  thisCase,
  setCase,
  userInfo,
  register,
  setValue,
  trigger,
  errors,
}: EducationSectionProps): JSX.Element => {
  const [disabled, setDisabled] = useState<boolean>(false);
  const [isEducationModalOpen, setIsEducationModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingEducation, setEditingEducation] = useState<any>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeEducationIndex, setActiveEducationIndex] = useState<number | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingEducation, setIsDeletingEducation] = useState(false);

  const educationList = consultationForm.educations || [];

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    // setActiveEducationIndex(null);
  };

  const handleEditEducation = () => {
    if (activeEducationIndex === null) return;

    setEditingEducation(educationList[activeEducationIndex]);
    setIsEducationModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteEducation = () => {
    if (activeEducationIndex === null) return;

    setIsDeleteModalOpen(true);
    handleMenuClose();
  };

  const confirmDeleteEducation = async () => {
    if (activeEducationIndex === null) return;

    const education = educationList[activeEducationIndex];
    if (!education?.id) return;

    try {
      setIsDeletingEducation(true);

      await deleteOneReportObject(thisCase.one_report.id, education.id, ObjectType.Education);

      setConsultationForm((prev) => ({
        ...prev,
        educations: prev.educations.filter((_, i) => i !== activeEducationIndex),
      }));

      setCase((prev) => ({
        ...prev,
        one_report: {
          ...prev.one_report,
          educations: prev.one_report.educations.filter((e) => e.id !== education.id),
        },
      }));

      setIsDeleteModalOpen(false);
    } catch (error) {
      displayErrorDetails('Failed to delete education');
    } finally {
      setIsDeletingEducation(false);
    }
  };

  const updateEducation = async (
    updatedEducation: Education,
    partialField: keyof IEducation,
    id?: string,
  ): Promise<void> => {
    try {
      const { educations: updatedEducations } = id
        ? await updateOneReportObject(thisCase?.one_report.id || '', ObjectType.Education, {
            ...updatedEducation,
            id,
          })
        : await addOneReportObject(thisCase?.one_report.id || '', ObjectType.Education, updatedEducation as IEducation);

      setCase((prevCase) => ({
        ...prevCase,
        one_report: {
          ...prevCase.one_report,
          educations: prevCase.one_report.educations.map((edu) =>
            edu.id === id
              ? {
                  ...edu,
                  [partialField]: updatedEducations.find((updatedEdu) => updatedEdu.id === id)?.[partialField],
                }
              : edu,
          ),
        },
      }));

      setConsultationForm((prevForm) => ({
        ...prevForm,
        educations: prevForm.educations.map((edu) =>
          edu.id === id
            ? { ...edu, [partialField]: updatedEducations.find((updatedEdu) => updatedEdu.id === id)?.[partialField] }
            : edu,
        ),
      }));
    } catch (error) {
      displayErrorDetails(`Error updating education: ${error}`);
    }
  };

  const handleSaveEducation = async (data: EducationForm) => {
    try {
      let updatedEducations: IEducation[] = [];

      if (editingEducation?.id) {
        // Editing existing education
        const res = await updateOneReportObject(thisCase.one_report.id, ObjectType.Education, {
          ...data,
          id: editingEducation.id,
        });
        updatedEducations = res.educations;
      } else {
        // Adding new education
        const res = await addOneReportObject(thisCase.one_report.id, ObjectType.Education, data as IEducation);
        updatedEducations = res.educations;
      }

      // Update both form and case state
      setConsultationForm((prev) => ({
        ...prev,
        educations: updatedEducations,
      }));

      setCase((prev) => ({
        ...prev,
        one_report: {
          ...prev.one_report,
          educations: updatedEducations,
        },
      }));
    } catch (error) {
      displayErrorDetails('Failed to save education');
    } finally {
      setEditingEducation(null);
      setIsEducationModalOpen(false);
    }
  };

  const addNewResourceField = async (): Promise<void> => {
    try {
      const { educations: res } = await addOneReportObject(thisCase?.one_report.id || '', ObjectType.Education, {
        url: '',
        title: '',
        id: '',
      });
      const sortedRes = res.sort((a, b) => Number(a.id?.valueOf() ?? Infinity) - Number(b.id?.valueOf() ?? Infinity));
      setConsultationForm((prevForm) => ({
        ...prevForm,
        educations: [...sortedRes],
      }));
    } catch (error) {
      displayErrorDetails(`Error adding new resource: ${error}`);
    }
  };

  const deleteEducation = async (id: string): Promise<void> => {
    try {
      if (id) {
        const res = await deleteOneReportObject(thisCase?.one_report.id || '', id, ObjectType.Education);
        if (res.educations.length === 0) {
          setValue(`educations`, []);
          trigger('educations');
        }
      }

      setConsultationForm((prevForm) => ({
        ...prevForm,
        educations: prevForm.educations
          .filter((education) => education.id !== id)
          .sort((a, b) => Number(a.id?.valueOf() ?? Infinity) - Number(b.id?.valueOf() ?? Infinity)),
      }));
    } catch (error) {
      displayErrorDetails(`Error deleting education: ${error}`);
    }
  };

  const handleEducationOnBlur = async (
    education: Education,
    partialEducation: keyof IEducation,
    id?: string,
  ): Promise<void> => {
    await updateEducation(education, partialEducation, id);
  };

  useEffect(() => {
    if (checkCollapsibleHeaderColor(thisCase, userInfo, 'Education') !== 'edit') {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [thisCase, userInfo]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, index: number) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveEducationIndex(index);
  };

  const handleAddEducation = () => {
    setEditingEducation(null);
    setIsEducationModalOpen(true);
  };

  return (
    <div
      className={`${styles.possibleTreatmentNeedsSectionDivChild} ${
        educationList.length > 0 ? styles.possibleTreatmentNeedsSectionDivChildExist : ''
      } `}
    >
      <div className={styles.possibleTreatmentNeedsSectionDivChildRow}>
        <span className={styles.possibleTreatmentNeedsSectionDivChildEducation}>
          <img src={Book} />
        </span>
        {educationList.length === 0 ? (
          <Button
            variant="text"
            onClick={handleAddEducation}
            disabled={checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Education') !== 'edit'}
          >
            Add education
          </Button>
        ) : (
          <div className={styles.existDataDiv}>
            <span>Education</span>
            <Button
              variant="text"
              onClick={handleAddEducation}
              disabled={checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Education') !== 'edit'}
            >
              <AddIcon /> Add
            </Button>
          </div>
        )}
      </div>
      {educationList.length > 0 && (
        <div className={styles.educationBody}>
          {educationList.map((education, index) => {
            return (
              <React.Fragment key={index}>
                <Divider orientation="horizontal" flexItem className={styles.divider} />
                <div className={styles.educationDetails}>
                  {normalizeUrl(education.url) ? (
                    <a href={normalizeUrl(education.url)} target="_blank" rel="noopener noreferrer">
                      {education.title} <InsertLinkIcon />
                    </a>
                  ) : (
                    <span>
                      {education.title} <InsertLinkIcon />
                    </span>
                  )}
                  {checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Education') === 'edit' && (
                    <MoreHorizIcon className={styles.moreIcon} onClick={(e: any) => handleMenuOpen(e, index)} />
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditEducation}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteEducation} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>
      <EducationModal
        open={isEducationModalOpen}
        onClose={() => {
          setEditingEducation(null);
          setIsEducationModalOpen(false);
        }}
        onSave={handleSaveEducation}
        initialData={editingEducation}
      />
      <ConfirmActionModal
        open={isDeleteModalOpen}
        title="Delete education"
        message={`Are you sure you want to delete ${
          activeEducationIndex !== null
            ? educationList[activeEducationIndex]?.title || 'this education item'
            : 'this education item'
        }?`}
        confirmText="Delete"
        cancelText="Cancel"
        destructive
        isLoading={isDeletingEducation}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDeleteEducation}
      />
    </div>
  );
};

export default React.memo(EducationSection);
